import * as THREE from 'three';

export type EnvironmentModel = {
  root: THREE.Group;
  tableTopY: number;
  presentationPlane: THREE.Mesh;
};

function roundedRectangle(width: number, height: number, radius: number): THREE.Shape {
  const x = -width / 2;
  const y = -height / 2;
  const shape = new THREE.Shape();
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  return shape;
}

function makeWoodTextures(): { albedo: THREE.CanvasTexture; roughness: THREE.DataTexture } {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Unable to create wood texture context');
  const gradient = context.createLinearGradient(0, 0, size, size);
  gradient.addColorStop(0, '#18121b');
  gradient.addColorStop(0.48, '#2b1b2e');
  gradient.addColorStop(1, '#141018');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);
  let seed = 0x4f1bbcdc;
  const random = () => {
    seed = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    seed ^= seed + Math.imul(seed ^ (seed >>> 7), 61 | seed);
    return ((seed ^ (seed >>> 14)) >>> 0) / 4294967296;
  };
  for (let index = 0; index < 92; index += 1) {
    const y = random() * size;
    context.strokeStyle = `rgba(${58 + Math.floor(random() * 30)},${31 + Math.floor(random() * 16)},${55 + Math.floor(random() * 24)},${0.08 + random() * 0.1})`;
    context.lineWidth = 0.4 + random() * 1.8;
    context.beginPath();
    context.moveTo(0, y);
    context.bezierCurveTo(size * 0.25, y + random() * 10 - 5, size * 0.7, y + random() * 12 - 6, size, y + random() * 8 - 4);
    context.stroke();
  }
  const albedo = new THREE.CanvasTexture(canvas);
  albedo.colorSpace = THREE.SRGBColorSpace;
  albedo.wrapS = albedo.wrapT = THREE.RepeatWrapping;
  albedo.repeat.set(2.4, 1.4);

  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const offset = (y * size + x) * 4;
      const grain = 160 + Math.floor(32 * Math.sin(y * 0.24 + Math.sin(x * 0.013) * 3));
      data[offset] = grain;
      data[offset + 1] = grain;
      data[offset + 2] = grain;
      data[offset + 3] = 255;
    }
  }
  const roughness = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  roughness.colorSpace = THREE.NoColorSpace;
  roughness.wrapS = roughness.wrapT = THREE.RepeatWrapping;
  roughness.repeat.copy(albedo.repeat);
  roughness.needsUpdate = true;
  return { albedo, roughness };
}

export function createEnvironment(): EnvironmentModel {
  const root = new THREE.Group();
  root.name = 'archiveEnvironment';
  const tableTopY = 0;
  const textures = makeWoodTextures();

  const tableGeometry = new THREE.ExtrudeGeometry(roundedRectangle(18, 10.8, 0.75), {
    depth: 0.55,
    bevelEnabled: true,
    bevelSegments: 4,
    bevelSize: 0.11,
    bevelThickness: 0.1,
    curveSegments: 12,
  });
  tableGeometry.translate(0, 0, -0.275);
  const table = new THREE.Mesh(
    tableGeometry,
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: textures.albedo,
      roughnessMap: textures.roughness,
      roughness: 0.72,
      metalness: 0.02,
      clearcoat: 0.16,
      clearcoatRoughness: 0.72,
    }),
  );
  table.name = 'walnutTable';
  table.rotation.x = Math.PI / 2;
  table.position.y = tableTopY - 0.31;
  table.receiveShadow = true;
  table.castShadow = true;
  root.add(table);

  const mat = new THREE.Mesh(
    new THREE.PlaneGeometry(10.6, 6.3),
    new THREE.MeshPhysicalMaterial({
      color: '#11101a',
      roughness: 0.86,
      metalness: 0.02,
      clearcoat: 0.1,
      clearcoatRoughness: 0.8,
    }),
  );
  mat.name = 'presentationMat';
  mat.rotation.x = -Math.PI / 2;
  mat.position.set(-1.9, tableTopY + 0.018, 0.15);
  mat.receiveShadow = true;
  root.add(mat);

  const trimMaterial = new THREE.MeshStandardMaterial({
    color: '#7057d8',
    emissive: '#291d62',
    emissiveIntensity: 0.85,
    roughness: 0.35,
    metalness: 0.34,
  });
  const trimSegments = [
    { size: [10.7, 0.018, 0.035] as const, position: [-1.9, tableTopY + 0.026, -3.02] as const },
    { size: [10.7, 0.018, 0.035] as const, position: [-1.9, tableTopY + 0.026, 3.32] as const },
    { size: [0.035, 0.018, 6.32] as const, position: [-7.27, tableTopY + 0.026, 0.15] as const },
    { size: [0.035, 0.018, 6.32] as const, position: [3.47, tableTopY + 0.026, 0.15] as const },
  ];
  for (const [index, segment] of trimSegments.entries()) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(...segment.size), trimMaterial);
    mesh.name = `matTrim${index}`;
    mesh.position.set(segment.position[0], segment.position[1], segment.position[2]);
    root.add(mesh);
  }

  const backdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(30, 16),
    new THREE.MeshStandardMaterial({
      color: '#090812',
      roughness: 0.96,
      metalness: 0,
    }),
  );
  backdrop.name = 'backdrop';
  backdrop.position.set(0, 7.8, -7.2);
  backdrop.receiveShadow = true;
  root.add(backdrop);

  const halo = new THREE.Mesh(
    new THREE.RingGeometry(3.8, 3.84, 96),
    new THREE.MeshBasicMaterial({
      color: '#6045cc',
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    }),
  );
  halo.name = 'archiveHalo';
  halo.position.set(-2.2, 5.5, -7.08);
  root.add(halo);

  const presentationPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(28, 18),
    new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide }),
  );
  presentationPlane.name = 'dragSurface';
  presentationPlane.rotation.x = -Math.PI / 2;
  presentationPlane.position.y = tableTopY + 0.22;
  root.add(presentationPlane);

  root.userData.dispose = () => {
    textures.albedo.dispose();
    textures.roughness.dispose();
  };
  return { root, tableTopY, presentationPlane };
}
